import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { resolveRedirect } from "./lib/redirects";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * www → apex 301 for the production domain. Search Console treats
 * www.comprehensivetaxresolution.com and the apex as separate sites, causing
 * duplicate canonical / title / H1 findings on the www host. Also emits HSTS.
 */
const wwwToApexMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const host = (request.headers.get("host") ?? url.host).toLowerCase();
  if (host === "www.comprehensivetaxresolution.com") {
    return new Response(null, {
      status: 301,
      headers: {
        location: `https://comprehensivetaxresolution.com${url.pathname}${url.search}`,
        "cache-control": "public, max-age=86400",
        "strict-transport-security": "max-age=31536000; includeSubDomains; preload",
      },
    });
  }
  return next();
});

/**
 * Legacy-URL redirect middleware. Runs on every request and 301s any
 * pathname listed in src/lib/redirects.ts to its new equivalent.
 * Preserves the query string so UTM params survive the redirect.
 */
const legacyRedirectMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const target = resolveRedirect(url.pathname);
  if (target && target !== url.pathname) {
    const location = target + (url.search || "");
    return new Response(null, {
      status: 301,
      headers: {
        location,
        "cache-control": "public, max-age=86400",
      },
    });
  }
  return next();
});


/**
 * Paid-media subdomain gate. On `get.comprehensivetax.com` (and any
 * subdomain starting with `get.`), only paid landing pages under `/p/*`
 * are exposed. All other paths redirect to the default lander so the
 * main marketing site stays hidden during the paid-media-only launch
 * phase. Internal paths (assets, api, robots, sitemap, favicon) pass
 * through untouched.
 *
 * The `.lovable.app` preview/published URL is NOT gated — full site
 * remains browsable there for internal QA.
 */
const DEFAULT_LANDER = "/p/get-help";
const PAID_HOST_PATTERN = /^get\./i;

function isInternalPath(pathname: string): boolean {
  return (
    pathname.startsWith("/p/") ||
    pathname.startsWith("/api/") ||
    pathname.startsWith("/_build/") ||
    pathname.startsWith("/_server/") ||
    pathname.startsWith("/assets/") ||
    pathname.startsWith("/lovable-") ||
    pathname === "/robots.txt" ||
    pathname === "/sitemap.xml" ||
    pathname === "/favicon.ico" ||
    pathname.startsWith("/favicon") ||
    pathname.startsWith("/apple-touch-icon") ||
    /\.[a-z0-9]{2,5}$/i.test(pathname)
  );
}

const paidHostGateMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const host = (request.headers.get("host") ?? url.host).toLowerCase();
  if (!PAID_HOST_PATTERN.test(host)) {
    return next();
  }
  if (isInternalPath(url.pathname)) {
    return next();
  }
  // Bot-friendly 302 (temporary) — when the full site relaunches on this
  // host we just remove the gate, no stale 301s cached anywhere.
  return new Response(null, {
    status: 302,
    headers: {
      location: DEFAULT_LANDER + (url.search || ""),
      "cache-control": "no-store",
    },
  });
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [wwwToApexMiddleware, paidHostGateMiddleware, legacyRedirectMiddleware, errorMiddleware],
}));


